/** "The answer was C": post-hoc scoring of a question asked aloud (owner, 2026-09-10).
 *
 * A question asked aloud carries no answer key, so before this the round showed the room and
 * nothing else — it marked the objective as taught but never moved anyone's mastery. The owner
 * wants those rounds to count. Saying afterwards which option was right is the smallest honest
 * way to get there:
 *
 * - it is a **label on the round**, never an edit: the responses stay exactly as the students
 *   sent them, and the engine is the authority (`score_item` → `item_keyed`);
 * - it is **explicit**: the teacher picks the option, in a dialog, having heard the room;
 * - it is **reversible** — "no right answer" takes it back off — until the results are
 *   released, after which the students have been told and a verdict may not change behind them;
 * - it tells **nobody** on its own. What a student sees is still decided by the reveal mode.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Sheet } from "../ui";
import type { AskedEntry } from "./useLive";

type Props = {
  entry: AskedEntry;
  onClose: () => void;
  onScore: (keys: string[]) => void;
};

export function ScoreAloudSheet({ entry, onClose, onScore }: Props) {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<string[]>(entry.correct_keys ?? []);
  const toggle = (k: string) => setKeys((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  return (
    <Sheet title={t("live.score.title")} lead={t("live.score.lead")} onClose={onClose}>
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          onScore(keys);
        }}
      >
        <p className="text-sm text-[color:var(--ink-2)]">
          {entry.spoken && !entry.stem ? t("live.aloud.spokenStem") : entry.stem}
        </p>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t("live.score.which")}</legend>
          <div className="grid-fit" style={{ "--fit-min": "7rem", gap: "0.5rem" } as React.CSSProperties}>
            {entry.option_keys.map((k) => (
              <Button
                key={k}
                variant={keys.includes(k) ? "primary" : "secondary"}
                aria-pressed={keys.includes(k)}
                onClick={() => toggle(k)}
              >
                {k}
              </Button>
            ))}
          </div>
          <p className="text-[color:var(--ink-2)] text-sm">{t("live.score.hint")}</p>
        </fieldset>
        <div className="flex flex-col gap-2">
          <Button block type="submit" disabled={keys.length === 0}>{t("live.score.save")}</Button>
          {entry.keyed && (
            <Button block variant="secondary" onClick={() => onScore([])}>{t("live.score.clear")}</Button>
          )}
          <Button block variant="tertiary" onClick={onClose}>{t("dialog.cancel")}</Button>
        </div>
      </form>
    </Sheet>
  );
}
